import { 
  GameStatistics, 
  GameResult, 
  UserPreferences, 
  AIDifficulty, 
  GameMode,
  Profile,
  GameSession 
} from '../types';
import { errorHandlingService } from './ErrorHandlingService';

export class LocalStorageManager {
  private static readonly STATISTICS_KEY = 'tic-tac-toe-statistics';
  private static readonly PREFERENCES_KEY = 'tic-tac-toe-preferences';
  private static readonly PROFILE_KEY = 'tic-tac-toe-profile';
  private static readonly OFFLINE_GAMES_KEY = 'tic-tac-toe-offline-games';
  private static readonly GAME_COUNT_KEY = 'tic-tac-toe-game-count';

  /**
   * Get game statistics from localStorage
   */
  getStatistics(): GameStatistics {
    try {
      const stored = localStorage.getItem(LocalStorageManager.STATISTICS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return this.validateStatistics(parsed);
      }
    } catch (error) {
      errorHandlingService.handleStorageError(
        error instanceof Error ? error : new Error(String(error)),
        { action: 'load_statistics' },
        { showNotification: false }
      );
    }
    
    return this.getDefaultStatistics();
  }

  /**
   * Save game statistics to localStorage
   */
  saveStatistics(statistics: GameStatistics): void {
    try {
      const validated = this.validateStatistics(statistics);
      localStorage.setItem(LocalStorageManager.STATISTICS_KEY, JSON.stringify(validated));
    } catch (error) {
      const storageError = errorHandlingService.handleStorageError(
        error instanceof Error ? error : new Error(String(error)),
        { action: 'save_statistics' }
      );
      throw new Error('Failed to save statistics');
    }
  }

  /**
   * Record a game result and update statistics
   */
  recordGameResult(gameResult: GameResult): GameStatistics {
    const currentStats = this.getStatistics();
    const updatedStats = this.updateStatisticsWithResult(currentStats, gameResult);
    this.saveStatistics(updatedStats);
    return updatedStats;
  }

  /**
   * Get user preferences from localStorage
   */
  getPreferences(): UserPreferences {
    try {
      const stored = localStorage.getItem(LocalStorageManager.PREFERENCES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return this.validatePreferences(parsed);
      }
    } catch (error) {
      console.warn('Failed to load preferences from localStorage:', error);
    }
    
    return this.getDefaultPreferences();
  }

  /**
   * Save user preferences to localStorage
   */
  savePreferences(preferences: UserPreferences): void {
    try {
      const validated = this.validatePreferences(preferences);
      localStorage.setItem(LocalStorageManager.PREFERENCES_KEY, JSON.stringify(validated));
    } catch (error) {
      console.error('Failed to save preferences to localStorage:', error);
      throw new Error('Failed to save preferences');
    }
  }

  /**
   * Get user profile from localStorage
   */
  getProfile(): Profile | null {
    try {
      const stored = localStorage.getItem(LocalStorageManager.PROFILE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return this.validateProfile(parsed);
      }
    } catch (error) {
      console.warn('Failed to load profile from localStorage:', error);
    }
    
    return null;
  }

  /**
   * Save user profile to localStorage
   */
  saveProfile(profile: Profile): void {
    try {
      const validated = this.validateProfile(profile);
      if (validated) {
        localStorage.setItem(LocalStorageManager.PROFILE_KEY, JSON.stringify(validated));
      }
    } catch (error) {
      console.error('Failed to save profile to localStorage:', error);
      throw new Error('Failed to save profile');
    }
  }

  /**
   * Get offline game sessions
   */
  getOfflineGames(): GameSession[] {
    try {
      const stored = localStorage.getItem(LocalStorageManager.OFFLINE_GAMES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed.map(this.validateGameSession).filter(Boolean) as GameSession[] : [];
      }
    } catch (error) {
      console.warn('Failed to load offline games from localStorage:', error);
    }
    
    return [];
  }

  /**
   * Save offline game session
   */
  saveOfflineGame(gameSession: GameSession): void {
    try {
      const offlineGames = this.getOfflineGames();
      const existingIndex = offlineGames.findIndex(game => game.id === gameSession.id);
      
      if (existingIndex >= 0) {
        offlineGames[existingIndex] = gameSession;
      } else {
        offlineGames.push(gameSession);
      }
      
      localStorage.setItem(LocalStorageManager.OFFLINE_GAMES_KEY, JSON.stringify(offlineGames));
    } catch (error) {
      console.error('Failed to save offline game to localStorage:', error);
      throw new Error('Failed to save offline game');
    }
  }

  /**
   * Remove offline game session
   */
  removeOfflineGame(gameId: string): void {
    try {
      const offlineGames = this.getOfflineGames();
      const filteredGames = offlineGames.filter(game => game.id !== gameId);
      localStorage.setItem(LocalStorageManager.OFFLINE_GAMES_KEY, JSON.stringify(filteredGames));
    } catch (error) {
      console.error('Failed to remove offline game from localStorage:', error);
    }
  }

  /**
   * Get guest game count (for free game limit)
   */
  getGuestGameCount(): number {
    try {
      const stored = localStorage.getItem(LocalStorageManager.GAME_COUNT_KEY);
      if (stored) {
        const count = parseInt(stored, 10);
        return isNaN(count) ? 0 : count;
      }
    } catch (error) {
      console.warn('Failed to load game count from localStorage:', error);
    }
    
    return 0;
  }

  /**
   * Increment guest game count
   */
  incrementGuestGameCount(): number {
    const currentCount = this.getGuestGameCount();
    const newCount = currentCount + 1;
    
    try {
      localStorage.setItem(LocalStorageManager.GAME_COUNT_KEY, newCount.toString());
    } catch (error) {
      console.error('Failed to save game count to localStorage:', error);
    }
    
    return newCount;
  }

  /**
   * Reset guest game count (when profile is created)
   */
  resetGuestGameCount(): void {
    try {
      localStorage.removeItem(LocalStorageManager.GAME_COUNT_KEY);
    } catch (error) {
      console.warn('Failed to reset game count in localStorage:', error);
    }
  }

  /**
   * Clear all stored data
   */
  clearAllData(): void {
    try {
      localStorage.removeItem(LocalStorageManager.STATISTICS_KEY);
      localStorage.removeItem(LocalStorageManager.PREFERENCES_KEY);
      localStorage.removeItem(LocalStorageManager.PROFILE_KEY);
      localStorage.removeItem(LocalStorageManager.OFFLINE_GAMES_KEY);
      localStorage.removeItem(LocalStorageManager.GAME_COUNT_KEY);
    } catch (error) {
      console.warn('Failed to clear data from localStorage:', error);
    }
  }

  /**
   * Check if localStorage is available
   */
  isStorageAvailable(): boolean {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get default statistics
   */
  private getDefaultStatistics(): GameStatistics {
    return {
      singlePlayer: {
        [AIDifficulty.EASY]: { wins: 0, losses: 0, draws: 0 },
        [AIDifficulty.MEDIUM]: { wins: 0, losses: 0, draws: 0 },
        [AIDifficulty.HARD]: { wins: 0, losses: 0, draws: 0 }
      },
      multiplayer: { wins: 0, losses: 0, draws: 0 },
      totalGames: 0,
      winRate: 0
    };
  }

  /**
   * Get default preferences
   */
  private getDefaultPreferences(): UserPreferences {
    return {
      theme: 'default',
      soundEnabled: true,
      defaultGameMode: GameMode.SINGLE_PLAYER,
      notifications: {
        turnBased: true,
        gameInvites: true,
        gameResults: true
      }
    };
  }

  /**
   * Update statistics with a new game result
   */
  private updateStatisticsWithResult(stats: GameStatistics, result: GameResult): GameStatistics {
    const updatedStats = { ...stats };
    
    // Determine if player won, lost, or drew
    const isDraw = result.isDraw;
    const isWin = !isDraw && result.winner === 'player';
    const isLoss = !isDraw && result.winner && result.winner !== 'player';

    if (result.mode === GameMode.SINGLE_PLAYER && result.difficulty) {
      const difficulty = result.difficulty as AIDifficulty;
      if (isDraw) {
        updatedStats.singlePlayer[difficulty].draws++;
      } else if (isWin) {
        updatedStats.singlePlayer[difficulty].wins++;
      } else if (isLoss) {
        updatedStats.singlePlayer[difficulty].losses++;
      }
    } else if (result.mode === GameMode.MULTIPLAYER) {
      if (isDraw) {
        updatedStats.multiplayer.draws++;
      } else if (isWin) {
        updatedStats.multiplayer.wins++;
      } else if (isLoss) {
        updatedStats.multiplayer.losses++;
      }
    }

    // Update total games and win rate
    updatedStats.totalGames++;
    const totalWins = Object.values(updatedStats.singlePlayer).reduce((sum, diff) => sum + diff.wins, 0) + updatedStats.multiplayer.wins;
    updatedStats.winRate = updatedStats.totalGames > 0 ? totalWins / updatedStats.totalGames : 0;

    return updatedStats;
  }

  /**
   * Validate statistics object
   */
  private validateStatistics(stats: any): GameStatistics {
    const defaults = this.getDefaultStatistics();
    
    if (!stats || typeof stats !== 'object') {
      return defaults;
    }

    const validated: GameStatistics = {
      singlePlayer: {
        [AIDifficulty.EASY]: this.validateDifficultyStats(stats.singlePlayer?.[AIDifficulty.EASY]) || defaults.singlePlayer[AIDifficulty.EASY],
        [AIDifficulty.MEDIUM]: this.validateDifficultyStats(stats.singlePlayer?.[AIDifficulty.MEDIUM]) || defaults.singlePlayer[AIDifficulty.MEDIUM],
        [AIDifficulty.HARD]: this.validateDifficultyStats(stats.singlePlayer?.[AIDifficulty.HARD]) || defaults.singlePlayer[AIDifficulty.HARD]
      },
      multiplayer: this.validateDifficultyStats(stats.multiplayer) || defaults.multiplayer,
      totalGames: typeof stats.totalGames === 'number' && stats.totalGames >= 0 ? stats.totalGames : defaults.totalGames,
      winRate: typeof stats.winRate === 'number' && stats.winRate >= 0 && stats.winRate <= 1 ? stats.winRate : defaults.winRate
    };

    return validated;
  }

  /**
   * Validate difficulty-specific statistics
   */
  private validateDifficultyStats(diffStats: any): { wins: number; losses: number; draws: number } | null {
    if (!diffStats || typeof diffStats !== 'object') {
      return null;
    }

    const wins = typeof diffStats.wins === 'number' && diffStats.wins >= 0 ? diffStats.wins : 0;
    const losses = typeof diffStats.losses === 'number' && diffStats.losses >= 0 ? diffStats.losses : 0;
    const draws = typeof diffStats.draws === 'number' && diffStats.draws >= 0 ? diffStats.draws : 0;

    return { wins, losses, draws };
  }

  /**
   * Validate preferences object
   */
  private validatePreferences(preferences: any): UserPreferences {
    const defaults = this.getDefaultPreferences();
    
    if (!preferences || typeof preferences !== 'object') {
      return defaults;
    }

    return {
      theme: typeof preferences.theme === 'string' ? preferences.theme : defaults.theme,
      soundEnabled: typeof preferences.soundEnabled === 'boolean' ? preferences.soundEnabled : defaults.soundEnabled,
      defaultGameMode: this.isValidGameMode(preferences.defaultGameMode) ? preferences.defaultGameMode : defaults.defaultGameMode,
      notifications: this.validateNotifications(preferences.notifications) || defaults.notifications
    };
  }

  /**
   * Validate game mode enum
   */
  private isValidGameMode(mode: any): mode is GameMode {
    return Object.values(GameMode).includes(mode);
  }

  /**
   * Validate notification settings
   */
  private validateNotifications(notifications: any): any {
    if (!notifications || typeof notifications !== 'object') {
      return null;
    }

    return {
      turnBased: typeof notifications.turnBased === 'boolean' ? notifications.turnBased : true,
      gameInvites: typeof notifications.gameInvites === 'boolean' ? notifications.gameInvites : true,
      gameResults: typeof notifications.gameResults === 'boolean' ? notifications.gameResults : true
    };
  }

  /**
   * Validate profile object
   */
  private validateProfile(profile: any): Profile | null {
    if (!profile || typeof profile !== 'object' || !profile.id) {
      return null;
    }

    return {
      id: profile.id,
      name: typeof profile.name === 'string' ? profile.name : '',
      avatar: typeof profile.avatar === 'string' ? profile.avatar : undefined,
      email: typeof profile.email === 'string' ? profile.email : undefined,
      authMethod: profile.authMethod,
      preferences: this.validatePreferences(profile.preferences),
      statistics: this.validateStatistics(profile.statistics),
      createdAt: profile.createdAt ? new Date(profile.createdAt) : new Date()
    };
  }

  /**
   * Validate game session object
   */
  private validateGameSession(session: any): GameSession | null {
    if (!session || typeof session !== 'object' || !session.id) {
      return null;
    }

    // Basic validation - in a real implementation, this would be more thorough
    return session as GameSession;
  }
}